import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Calculator, Check, ChevronRight, ChevronLeft, Sparkles, MessageCircle, Phone, Download, Calendar, X } from 'lucide-react';
import {
  trackPricingCompareStart,
  trackPricingCompareSubmit,
  trackProposalDownload,
  trackWhatsAppAfterQuote,
  trackCallAfterQuote,
  trackConsultationAfterQuote,
  buildWhatsAppUrl,
} from '../utils/analytics';

const API = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = '917009076796';

const MARKETPLACES = [
  { id: 'myntra', name: 'Myntra', icon: '👗', complexity: 1.0 },
  { id: 'nykaa', name: 'Nykaa', icon: '💄', complexity: 1.1 },
  { id: 'ajio', name: 'AJIO', icon: '🛍️', complexity: 1.0 },
  { id: 'amazon', name: 'Amazon', icon: '📦', complexity: 0.9 },
  { id: 'flipkart', name: 'Flipkart', icon: '🛒', complexity: 0.9 },
  { id: 'blinkit', name: 'Blinkit', icon: '⚡', complexity: 1.2 },
  { id: 'instamart', name: 'Instamart', icon: '🚀', complexity: 1.2 },
  { id: 'zepto', name: 'Zepto', icon: '⏱️', complexity: 1.2 },
];

const SERVICES = [
  { id: 'approval', name: 'Brand Approval / Registration', basePrice: 25000 },
  { id: 'account_mgmt', name: 'Account Management', basePrice: 45000 },
  { id: 'cataloging', name: 'Cataloging', basePrice: 35000 },
  { id: 'pla_ads', name: 'PLA / Sponsored Ads', basePrice: 40000 },
  { id: 'recon', name: 'Reconciliation', basePrice: 22000 },
  { id: 'listing_opt', name: 'Listing Optimization', basePrice: 28000 },
  { id: 'consulting', name: 'Strategy Consulting', basePrice: 30000 },
];

const TIERS = [
  { id: 'standard', name: 'Standard', mult: 1.0, perks: ['1 marketplace', 'Monthly review', 'Email support', 'Standard SLAs'] },
  { id: 'growth', name: 'Growth', mult: 1.75, perks: ['Up to 3 marketplaces', 'Weekly reviews', 'Dedicated AM', 'Slack support', 'PLA + Catalog included'], featured: true },
  { id: 'enterprise', name: 'Enterprise', mult: 2.55, perks: ['All marketplaces', 'Daily ops', 'Dedicated 3-person pod', 'Founder access', 'Custom integrations'] },
];

const REVENUE_BANDS = [
  { id: 'under_5L', label: 'Under ₹5L', recommends: 'standard' },
  { id: '5_20L', label: '₹5L – ₹20L', recommends: 'growth' },
  { id: '20_50L', label: '₹20L – ₹50L', recommends: 'growth' },
  { id: '50L_1cr', label: '₹50L – ₹1Cr', recommends: 'enterprise' },
  { id: '1cr_plus', label: '₹1Cr +', recommends: 'enterprise' },
];

const Step = ({ n, label, current }) => (
  <div className={`flex items-center gap-2 text-xs uppercase tracking-wide font-semibold ${current ? 'text-rose-600' : 'text-slate-400'}`}>
    <span className={`w-6 h-6 rounded-full flex items-center justify-center ${current ? 'bg-rose-600 text-white' : 'bg-slate-200 text-slate-500'}`}>{n}</span>
    <span className="hidden sm:inline">{label}</span>
  </div>
);

/**
 * PricingWidgetCore — reusable 4-step Lead Capture Engine.
 *
 * Props:
 *   embedded   — if true, omits Helmet + page-level hero/title (for modal use)
 *   onClose    — optional dismiss callback (passed by StickyQuoteButton modal)
 *   source     — tracking source label ('page' | 'sticky-modal' | 'cta-block')
 */
export function PricingWidgetCore({ embedded = false, onClose, source = 'page' }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [marketplace, setMarketplace] = useState(null);
  const [services, setServices] = useState([]);
  const [tier, setTier] = useState('growth');
  const [revenue, setRevenue] = useState(null);
  const [form, setForm] = useState({ name: '', brand: '', phone: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [proposalId, setProposalId] = useState(null);

  useEffect(() => { trackPricingCompareStart(source); }, [source]);

  // Auto-bump recommended tier when user picks revenue band
  useEffect(() => {
    if (revenue) {
      const band = REVENUE_BANDS.find((b) => b.id === revenue);
      if (band) setTier(band.recommends);
    }
  }, [revenue]);

  const prices = useMemo(() => {
    const mp = MARKETPLACES.find((m) => m.id === marketplace);
    const complexity = mp ? mp.complexity : 1;
    const baseSum = services.reduce((acc, sid) => {
      const s = SERVICES.find((x) => x.id === sid);
      return acc + (s ? s.basePrice : 0);
    }, 0);
    const calibrated = Math.round(baseSum * complexity);
    return TIERS.reduce((acc, t) => {
      acc[t.id] = Math.round((calibrated * t.mult) / 1000) * 1000;
      return acc;
    }, {});
  }, [marketplace, services]);

  const recommendedTier = revenue ? (REVENUE_BANDS.find((b) => b.id === revenue) || {}).recommends : null;

  const canProceed1 = !!marketplace;
  const canProceed2 = services.length > 0;
  const canSubmit = form.name && form.brand && form.phone && form.email && /^\S+@\S+\.\S+$/.test(form.email);

  const toggleService = (id) => setServices((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);

  const submitLead = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(`${API}/api/pricing-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          marketplace,
          services,
          tier,
          monthly_revenue: revenue || '',
          quoted_price: prices[tier],
          source_path: window.location.pathname + window.location.search + ' · ' + source,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setProposalId(data.id || `quote-${Date.now()}`);
      setSubmitted(true);
      trackPricingCompareSubmit(marketplace, services, tier, revenue);
    } catch (e) {
      setSubmitError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const downloadProposal = () => {
    trackProposalDownload(proposalId);
    window.open(`${API}/api/pricing-quote/${proposalId}/proposal.pdf`, '_blank');
  };

  const openWhatsApp = () => {
    const mp = MARKETPLACES.find((m) => m.id === marketplace);
    const msg = `Hi! I just got a ${tier.toUpperCase()} pricing quote for ${mp?.name || marketplace} — ₹${prices[tier].toLocaleString('en-IN')}. Brand: ${form.brand}. I'd like to discuss the proposal.`;
    trackWhatsAppAfterQuote(proposalId);
    window.open(buildWhatsAppUrl(WHATSAPP_NUMBER, msg), '_blank');
  };

  const bookConsultation = () => {
    trackConsultationAfterQuote(proposalId);
    if (onClose) onClose();
    navigate(`/contact?source=pricing-quote&quote_id=${proposalId}`);
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 sm:p-10 relative" data-testid="pricing-widget">
      {/* Close button for modal mode */}
      {embedded && onClose ? (
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
          aria-label="Close"
          data-testid="modal-close"
        >
          <X className="w-5 h-5 text-slate-600" />
        </button>
      ) : null}

      {/* Stepper */}
      <div className="flex items-center justify-center gap-3 sm:gap-5 mb-6">
        <Step n="1" label="Marketplace" current={step >= 1} />
        <ChevronRight className="w-4 h-4 text-slate-300" />
        <Step n="2" label="Services" current={step >= 2} />
        <ChevronRight className="w-4 h-4 text-slate-300" />
        <Step n="3" label="Package" current={step >= 3} />
        <ChevronRight className="w-4 h-4 text-slate-300" />
        <Step n="4" label="Proposal" current={step >= 4} />
      </div>

      {/* STEP 1 */}
      {step === 1 && (
        <div data-testid="pricing-step-1">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Which marketplace do you want to sell on?</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {MARKETPLACES.map((m) => (
              <button key={m.id} onClick={() => setMarketplace(m.id)}
                className={`p-4 rounded-xl border-2 transition-all text-center ${marketplace === m.id ? 'border-rose-600 bg-rose-50 shadow-md' : 'border-slate-200 hover:border-rose-300 hover:bg-rose-50/50'}`}
                data-testid={`marketplace-${m.id}`}>
                <div className="text-3xl mb-2">{m.icon}</div>
                <div className="font-semibold text-slate-900">{m.name}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* STEP 2 */}
      {step === 2 && (
        <div data-testid="pricing-step-2">
          <h2 className="text-lg font-semibold text-slate-800 mb-1">Which services do you need?</h2>
          <p className="text-sm text-slate-500 mb-4">Select all that apply.</p>
          <div className="space-y-2">
            {SERVICES.map((s) => {
              const selected = services.includes(s.id);
              return (
                <button key={s.id} onClick={() => toggleService(s.id)}
                  className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all text-left ${selected ? 'border-rose-600 bg-rose-50 shadow-sm' : 'border-slate-200 hover:border-rose-300'}`}
                  data-testid={`service-${s.id}`}>
                  <span className="font-medium text-slate-900">{s.name}</span>
                  {selected ? <Check className="w-5 h-5 text-rose-600" /> : <div className="w-5 h-5 rounded border-2 border-slate-300" />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* STEP 3 — Revenue qualifier + tier selection */}
      {step === 3 && (
        <div data-testid="pricing-step-3">
          <h2 className="text-lg font-semibold text-slate-800 mb-2">Monthly marketplace revenue?</h2>
          <p className="text-xs text-slate-500 mb-3">Helps us recommend the right package size.</p>
          <div className="flex flex-wrap gap-2 mb-6">
            {REVENUE_BANDS.map((b) => (
              <button key={b.id} onClick={() => setRevenue(b.id)}
                className={`px-3 py-1.5 rounded-full text-sm border-2 transition-all ${revenue === b.id ? 'border-rose-600 bg-rose-50 text-rose-800 font-semibold' : 'border-slate-200 text-slate-600 hover:border-rose-300'}`}
                data-testid={`revenue-${b.id}`}>
                {b.label}
              </button>
            ))}
          </div>

          <h2 className="text-lg font-semibold text-slate-800 mb-4">Choose your package tier</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {TIERS.map((t) => (
              <button key={t.id} onClick={() => setTier(t.id)}
                className={`p-5 rounded-xl border-2 text-left transition-all relative ${tier === t.id ? 'border-rose-600 shadow-lg' : 'border-slate-200 hover:border-rose-300'} ${t.featured ? 'bg-gradient-to-br from-rose-50 to-orange-50' : 'bg-white'}`}
                data-testid={`tier-${t.id}`}>
                {t.featured && !recommendedTier && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-rose-600 text-white text-xs font-bold rounded-full">MOST POPULAR</div>
                )}
                {recommendedTier === t.id && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-emerald-600 text-white text-xs font-bold rounded-full" data-testid={`recommended-${t.id}`}>
                    RECOMMENDED FOR YOUR SIZE
                  </div>
                )}
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{t.name}</div>
                <div className="text-3xl font-bold text-slate-900 mt-1">₹{prices[t.id]?.toLocaleString('en-IN')}</div>
                <div className="text-xs text-slate-500 mb-3">/ month</div>
                <ul className="space-y-1.5">
                  {t.perks.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* STEP 4 — Lead capture */}
      {step === 4 && !submitted && (
        <div data-testid="pricing-step-4">
          <h2 className="text-lg font-semibold text-slate-800 mb-1">Get your detailed proposal</h2>
          <p className="text-sm text-slate-500 mb-4">Line-item pricing, SLAs, case studies & next steps.</p>
          <div className="bg-slate-50 rounded-xl p-4 mb-5 border border-slate-200">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Your Quote</span>
              <span className="text-xs px-2 py-0.5 bg-rose-100 text-rose-800 rounded-full font-semibold">{tier.toUpperCase()}</span>
            </div>
            <div className="text-2xl font-bold text-slate-900">₹{prices[tier]?.toLocaleString('en-IN')}<span className="text-sm text-slate-500 font-normal"> / month</span></div>
            <div className="text-xs text-slate-600 mt-1">
              {MARKETPLACES.find((m) => m.id === marketplace)?.name} · {services.length} service{services.length !== 1 ? 's' : ''}
              {revenue ? ` · Revenue: ${REVENUE_BANDS.find((b) => b.id === revenue)?.label}` : ''}
            </div>
          </div>
          <div className="space-y-3">
            <input type="text" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-rose-500 focus:ring-2 focus:ring-rose-200 outline-none" data-testid="form-name" />
            <input type="text" placeholder="Brand name" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })}
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-rose-500 focus:ring-2 focus:ring-rose-200 outline-none" data-testid="form-brand" />
            <input type="tel" placeholder="Phone (+91…)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-rose-500 focus:ring-2 focus:ring-rose-200 outline-none" data-testid="form-phone" />
            <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-rose-500 focus:ring-2 focus:ring-rose-200 outline-none" data-testid="form-email" />
          </div>
          {submitError && <div className="mt-3 p-3 bg-rose-50 border border-rose-200 rounded-lg text-sm text-rose-700">{submitError}</div>}
          <button onClick={submitLead} disabled={!canSubmit || submitting}
            className="w-full mt-5 px-6 py-4 bg-gradient-to-r from-rose-600 to-orange-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
            data-testid="form-submit">
            {submitting ? 'Generating proposal…' : (<><Sparkles className="w-5 h-5" /> Get Detailed Proposal & Commercials</>)}
          </button>
          <p className="text-xs text-slate-500 text-center mt-2">We respect your data — never spam, never sold.</p>
        </div>
      )}

      {/* SUCCESS */}
      {step === 4 && submitted && (
        <div className="text-center py-4" data-testid="pricing-success">
          <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-4">
            <Check className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Proposal generated!</h2>
          <p className="text-sm text-slate-600 mb-6">Quote ID: <span className="font-mono">{proposalId}</span></p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-3xl mx-auto">
            <button onClick={downloadProposal} className="px-3 py-3 bg-slate-900 text-white rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-slate-800 text-sm" data-testid="success-download">
              <Download className="w-4 h-4" /> PDF
            </button>
            <button onClick={openWhatsApp} className="px-3 py-3 bg-[#25D366] text-white rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-[#128C7E] text-sm" data-testid="success-whatsapp">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </button>
            <a href="tel:+917009076796" onClick={() => trackCallAfterQuote(proposalId)}
              className="px-3 py-3 bg-rose-600 text-white rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-rose-700 text-sm" data-testid="success-call">
              <Phone className="w-4 h-4" /> Call
            </a>
            <button onClick={bookConsultation} className="px-3 py-3 bg-indigo-600 text-white rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-indigo-700 text-sm" data-testid="success-consult">
              <Calendar className="w-4 h-4" /> Book Call
            </button>
          </div>
        </div>
      )}

      {/* Nav */}
      {!submitted && (
        <div className="flex justify-between items-center mt-8 pt-6 border-t border-slate-200">
          <button onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1}
            className="px-4 py-2 text-slate-600 font-medium hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1" data-testid="nav-back">
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          {step < 4 ? (
            <button onClick={() => setStep(step + 1)}
              disabled={(step === 1 && !canProceed1) || (step === 2 && !canProceed2)}
              className="px-6 py-2.5 bg-rose-600 text-white font-bold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-rose-700 transition-colors flex items-center gap-1" data-testid="nav-next">
              Next <ChevronRight className="w-4 h-4" />
            </button>
          ) : <div />}
        </div>
      )}
    </div>
  );
}

/**
 * Standalone page wrapper. Default export = the /compare-pricing route component.
 */
export default function ComparePricingPage() {
  return (
    <>
      <Helmet>
        <title>Compare Marketplace Pricing | Get Custom Quote | Zaroori Retail</title>
        <meta name="description" content="Compare pricing across Myntra, Nykaa, AJIO, Amazon, Blinkit, Zepto. Get a custom 3-tier package quote for account management, ads, cataloging & more in 60 seconds." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://zarooriretail.com/compare-pricing" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-rose-50 to-orange-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-rose-200 mb-4">
              <Calculator className="w-4 h-4 text-rose-600" />
              <span className="text-xs uppercase tracking-wider font-semibold text-rose-600">60-second quote</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-2">Compare Marketplace Pricing</h1>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">Pick your marketplace + services and get an instant 3-tier package estimate. Detailed proposal sent on submission.</p>
          </div>
          <PricingWidgetCore source="page" />
        </div>
      </div>
    </>
  );
}
